import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import Number from '../Number/Number';
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { useMemo, useCallback } from 'react';

interface NumberControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: number|null): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const NumberControl = React.memo(({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: NumberControlProps) => {

    const jsonForms = useJsonForms();

    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.Number, data, uischema.scope, jsonForms, enabled && visible),
        [Component.Number, data, uischema.scope, jsonForms, enabled, visible]
    );

    // Memoize the change handler
    const handleNumberChange = useCallback((ev: any) => {
        const newValue = ev.target.value ? parseFloat(ev.target.value) : null;
        handleChange(path, newValue);
    }, [handleChange, path]);

    // Memoize the Number component props
    const numberProps = useMemo(() => ({
        variant: 'outlined' as const,
        error: errors,
        value: value,
        required: required,
        label: uischema.label,
        description: schema.description,
        onChange: handleNumberChange,
        minimum: schema.minimum,
        maximum: schema.maximum,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        errors,
        value,
        required,
        uischema.label,
        schema.description,
        handleNumberChange,
        schema.minimum,
        schema.maximum,
        enabled,
        visible,
        appValidated
    ]);

    return <Number {...numberProps} />;
});

// Export with memoization
export default React.memo(withJsonFormsControlProps(NumberControl));
